import { cn } from "cn"

import IconMicrophoneMuted from "@/components/icons/microphone-muted"
import IconPause from "@/components/icons/pause"
import IconPlayFill from "@/components/icons/play-fill"
import { Button } from "@/components/ui/button"
import { IconButton } from "@/components/ui/icon-button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

// The call controls — pause, mute, end — as voice mode's overlay and the setup
// voice interview both show them. Props only: each surface binds the toggles
// to its own session (the app store for voice mode, the interview's adapter
// for the interview) and End to its own way out, named for where that way
// out lands: "End" for voice mode, "Skip voice chat" for the setup interview,
// whose exit is the typed form (iOS labels the same row the same way per
// flow). Shared so the interview reads as the same kind of call; a second
// copy of the row is how that would quietly stop being true.
export function VoiceCallControls({
  paused,
  muted,
  pausable,
  mutable = true,
  onPauseToggle,
  onMuteToggle,
  onEnd,
  endLabel = "End",
  endTooltip,
  className,
}: {
  paused: boolean
  muted: boolean
  pausable: boolean
  // False while a session's mic is held by something a toggle can't undo —
  // the interview's closing mute — so the button doesn't offer a lie.
  mutable?: boolean
  onPauseToggle: () => void
  onMuteToggle: () => void
  onEnd: () => void
  endLabel?: string
  // No tooltip when omitted — a label that already says where End lands
  // ("Skip voice chat") has nothing to add on hover.
  endTooltip?: string
  className?: string
}) {
  const endButton = (
    <Button
      type="button"
      variant="subtle"
      size="md"
      className="gap-2"
      onClick={onEnd}
    >
      {endLabel}
    </Button>
  )
  return (
    <div
      role="group"
      aria-label="Call controls"
      className={cn("flex items-center justify-center gap-3", className)}
    >
      {/* Pause/resume the assistant: it stops speaking and resume re-speaks
        the cut-off remainder verbatim (VoiceChatRTC.setPaused). Pausing
        also mutes the mic — a pause silences the whole session — and
        resume restores only a mute the pause itself made (mutedByPause).
        Glyph is the ACTION, not the state. At rest an
        outline pause on the subtle surface; paused flips to the filled
        play on the primary (black) surface — the one loud state, since the
        session is standing still (design call, 2026-08-25). Only live
        while a substantial turn is being spoken (selectVoicePausable) —
        not for the short narrations bracketing a tool wait, whose
        enable/disable cycling read as flicker (resume always stays live). */}
      <IconButton
        variant={paused ? "primary" : "subtle"}
        size="md"
        icon={paused ? IconPlayFill : IconPause}
        label={paused ? "Resume" : "Pause"}
        tooltip
        disabled={!pausable}
        onClick={onPauseToggle}
      />
      {/* One glyph for both states: the slashed mic. The filled red surface
        is what shows you're muted — the mic being off is the one state in
        the session that stops it working, so it reads as an alarm rather
        than just an active toggle. Live through a pause, where it lights
        up from the pause's auto-mute: unmuting reopens the uplink WITHOUT
        resuming the held answer, so the customer can just keep talking —
        speaking ends the pause and the assistant answers that instead. */}
      <IconButton
        variant={muted ? "destructive" : "subtle"}
        size="md"
        icon={IconMicrophoneMuted}
        label={muted ? "Unmute microphone" : "Mute microphone"}
        tooltip
        disabled={!mutable}
        onClick={onMuteToggle}
      />
      {endTooltip ? (
        <Tooltip>
          <TooltipTrigger render={endButton} />
          <TooltipContent>
            <span>{endTooltip}</span>
          </TooltipContent>
        </Tooltip>
      ) : (
        endButton
      )}
    </div>
  )
}
